import { useLocalePathname } from "@i18n/routing";
import { canUseVideoResolution } from "@repo/config";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { usePostGenerationPaywall } from "@saas/payments/hooks/use-post-generation-paywall";
import { normalizePage } from "@saas/payments/lib/track-page";
import type {
	GenerationHistoryItem,
	HistoryFilter,
	VideoHistoryItem,
} from "@saas/shared/history/types";
import {
	GENERATION_HISTORY_QUERY_KEY,
	historyQueryKey,
} from "@saas/shared/history/types";
import { useResumeHistoryTasks } from "@saas/shared/history/use-resume-history-tasks";
import { apiClient } from "@shared/lib/api-client";
import { compressImage } from "@shared/lib/image-compression";
import {
	saveBlobPendingState,
	takeBlobPendingState,
} from "@shared/lib/pending-state";
import {
	uploadImageToR2Directly,
	uploadReferenceMediaToR2Directly,
} from "@shared/lib/r2-direct-upload";
import { reportGenerationFailure } from "@shared/lib/report-generation-failure";
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type {
	I2VAspectRatio,
	I2VMode,
	I2VModelId,
	I2VResolution,
	I2VRowMetadata,
} from "../types";
import {
	DEFAULT_I2V_ASPECT_RATIO,
	DEFAULT_I2V_MODEL,
	carryI2VModelToMode,
	clampI2VAspectRatio,
	clampI2VDuration,
	clampI2VResolution,
	getBillableVideoSeconds,
	getI2VAspectRatios,
	getI2VCreditCost,
	getI2VDefaultResolution,
	getI2VModel,
	getI2VPromptMaxLength,
	getMaxI2VImages,
	isI2VHistoryRow,
	isI2VModelId,
	resolveHistoryRowMode,
	resolveHistoryRowModelId,
	supportsI2VReferenceAudio,
	supportsI2VReferenceVideo,
} from "../types";

/** 一张待生成的输入图：本地选的文件还没上传，从素材库选的已经在 R2 上 */
export interface I2VInputImage {
	/** blob: 地址（本地文件）或 R2 地址（已上传） */
	previewUrl: string;
	/** 只有本地选的文件有，生成时才上传 */
	file?: File;
	/** 本地文件第一次生成时传到 R2 的地址。再点 Create 直接用，不然每点一次 My Assets 多一张同样的图 */
	uploadedUrl?: string;
}

export type I2VReferenceMediaKind = "video" | "audio";

/**
 * 一段参考视频或参考音频（2026-10-10 起，只在 reference tab）。选完立刻在浏览器转码 +
 * 上传——转码要几秒，拖到点生成时再做太慢——生成时直接用 R2 地址。
 * url 和 durationSec 没齐之前算「处理中」，不能提交。
 */
export interface I2VReferenceMedia {
	id: string;
	kind: I2VReferenceMediaKind;
	/** 转码后的 blob: 地址或 R2 地址，视频显示首帧用；转码完成前为 null */
	previewUrl: string | null;
	durationSec: number;
	url: string | null;
	/** 0–1：转码占前 90%，上传占最后 10% */
	progress: number;
}

/** 历史回填时量一下时长，只读元数据，不需要 CORS；失败返回 null */
function loadMediaDuration(
	url: string,
	kind: I2VReferenceMediaKind,
): Promise<number | null> {
	return new Promise((resolve) => {
		const media = document.createElement(kind);
		media.preload = "metadata";
		media.onloadedmetadata = () =>
			resolve(Number.isFinite(media.duration) ? media.duration : null);
		media.onerror = () => resolve(null);
		media.src = url;
	});
}

function revokeMedia(items: I2VReferenceMedia[]) {
	for (const item of items) {
		if (item.previewUrl) {
			revokeIfBlob(item.previewUrl);
		}
	}
}

function referenceMediaErrorMessage(
	error: unknown,
	kind: I2VReferenceMediaKind,
	minSeconds: number,
): string {
	switch ((error as { code?: string } | null)?.code) {
		case "unsupported-browser":
			return "Your browser can't process videos. Please use the latest Chrome, Edge or Safari.";
		case "unreadable":
			return kind === "video"
				? "We couldn't read this video. Please try an MP4 or MOV file."
				: "We couldn't read this audio. Please try an MP3 or WAV file.";
		case "too-short":
			return `Each reference ${kind} must be at least ${minSeconds}s long.`;
		case "aspect-ratio":
			return "Video aspect ratio must be between 2:5 and 5:2.";
		default:
			return `Failed to process the ${kind}. Please try again.`;
	}
}

/** 这档在这个 tab 收不收某一类参考媒体 */
function supportsMediaKind(
	model: I2VModelId,
	mode: I2VMode,
	kind: I2VReferenceMediaKind,
): boolean {
	return kind === "video"
		? supportsI2VReferenceVideo(model, mode)
		: supportsI2VReferenceAudio(model, mode);
}

function mediaLimits(model: I2VModelId, kind: I2VReferenceMediaKind) {
	const config = getI2VModel(model);
	return kind === "video" ? config.referenceVideo : config.referenceAudio;
}

/**
 * start / between 是固定槽位（null = 空槽），reference 默认是一个不带空洞的列表；
 * 预设页传 referenceSlots 后 reference 也变成固定槽位（Left / Right 这种有语义的位子）
 */
function emptySlotsFor(
	mode: I2VMode,
	referenceSlots?: number,
): (I2VInputImage | null)[] {
	if (mode === "start") {
		return [null];
	}
	if (mode === "between") {
		return [null, null];
	}
	if (mode === "reference" && referenceSlots) {
		return Array.from({ length: referenceSlots }, () => null);
	}
	return [];
}

function revokeIfBlob(url: string) {
	if (url.startsWith("blob:")) {
		URL.revokeObjectURL(url);
	}
}

function revokeAll(images: (I2VInputImage | null)[]) {
	for (const image of images) {
		if (image) {
			revokeIfBlob(image.previewUrl);
		}
	}
}

const PENDING_STATE_KEY = "pendingImageToVideoState";
const PENDING_STATE_TTL = 3600000;
const HISTORY_QUERY_KEY = GENERATION_HISTORY_QUERY_KEY;
const HISTORY_PAGE_SIZE = 8;
/** Consecutive poll failures before we stop polling a task */
const MAX_POLL_FAILURES = 3;

/** 一张待恢复的输入图：本地选的存 Blob，素材库选的只存 R2 地址 */
type PendingI2VImage =
	| { kind: "empty" }
	| { kind: "file"; blob: Blob; name: string }
	| { kind: "url"; url: string };

interface PendingI2VState {
	mode: string;
	model: string;
	images: PendingI2VImage[];
	prompt: string;
	resolution: string;
	aspectRatio: string;
	duration: number;
	outputs: number;
	generateAudio?: boolean;
	/** 已上传完的参考视频 / 音频（2026-10-10 起） */
	media?: { kind: I2VReferenceMediaKind; url: string; durationSec: number }[];
}

/** 只读尺寸，跨域图不需要 CORS；加载失败返回 null */
function loadAspectRatio(url: string): Promise<number | null> {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () =>
			resolve(img.naturalWidth / img.naturalHeight || null);
		img.onerror = () => resolve(null);
		img.src = url;
	});
}

export type I2VPaywallReason =
	| "insufficient-credits"
	| "video-resolution"
	| "post-generation";

/**
 * 历史行 / 登录前存下的现场里的 mode 字符串 → 本页能用的 mode。
 * /image-to-video 没有文生视频 tab，文生视频的记录回填到那页时落回单图模式。
 */
function toSupportedMode(raw: string | undefined, allowText: boolean): I2VMode {
	if (raw === "between" || raw === "reference") {
		return raw;
	}
	if (raw === "text" && allowText) {
		return "text";
	}
	return "start";
}

export interface UseImageToVideoOptions {
	/** 只有首页 hero 开文生视频；/image-to-video 保持三种图生视频范式 */
	allowTextMode?: boolean;
	/**
	 * 预设页（如 hotel lobby）首屏直接落在这组值上。不能 mount 后再 setMode /
	 * setModel：切 mode 会清图，setModel 用的是旧 mode 闭包，而且会闪一下默认表单
	 */
	initial?: {
		mode: I2VMode;
		model: I2VModelId;
		resolution?: I2VResolution;
		aspectRatio?: I2VAspectRatio;
		duration?: number;
		prompt?: string;
	};
	/**
	 * reference 模式改成固定槽位数（null = 空槽），配合 setImageAt 用。
	 * 不传保持原样：空列表 + addImageFiles / addExistingImages 追加
	 */
	referenceSlots?: number;
	/**
	 * 固定槽位里前几个是必填（AI Kiss 页：合照一张就够，第二槽选填）。
	 * 不传就是每个槽都得填
	 */
	requiredReferenceSlots?: number;
	/**
	 * 带着参考视频时只把前几张图发给模型（Bulin 47：第二槽默认场景图只给 Lite 用，视频档带着它
	 * H3 会拿它拍一个开场镜头再切进原片）。不传就是全部
	 */
	referenceVideoImageSlots?: number;
	/**
	 * reference 模式视频和照片都必填（/genjutsu-ai：改的是用户的视频，换进去的是照片里的人 / 物）；
	 * 时长和比例也跟着视频走（时长取整后夹到模型范围），成片和原片一样长
	 */
	requireReferenceVideo?: boolean;
	/** 登录前现场的 IndexedDB key；预设页用自己的，免得和 /image-to-video 互相串 */
	pendingStateKey?: string;
}

/**
 * `historyFilter` narrows the feed to the active tab. Filtering runs on the
 * server because the feed is infinite-scrolled.
 */
export function useImageToVideo(
	historyFilter: HistoryFilter = "all",
	{
		allowTextMode = false,
		initial,
		referenceSlots,
		requiredReferenceSlots,
		referenceVideoImageSlots,
		requireReferenceVideo = false,
		pendingStateKey,
	}: UseImageToVideoOptions = {},
) {
	const [mode, setModeState] = useState<I2VMode>(initial?.mode ?? "start");
	const [model, setModelState] = useState<I2VModelId>(
		() => initial?.model ?? DEFAULT_I2V_MODEL[initial?.mode ?? "start"],
	);
	const [inputImages, setInputImages] = useState<(I2VInputImage | null)[]>(
		() => emptySlotsFor(initial?.mode ?? "start", referenceSlots),
	);
	const [referenceMedia, setReferenceMedia] = useState<I2VReferenceMedia[]>(
		[],
	);
	const [prompt, setPrompt] = useState(initial?.prompt ?? "");
	const [resolution, setResolutionState] = useState<I2VResolution>(
		() => initial?.resolution ?? getI2VDefaultResolution(model),
	);
	const [aspectRatioState, setAspectRatio] = useState<I2VAspectRatio>(() =>
		clampI2VAspectRatio(
			model,
			initial?.aspectRatio ?? DEFAULT_I2V_ASPECT_RATIO,
			mode,
		),
	);

	const [duration, setDuration] = useState<number>(
		() => initial?.duration ?? getI2VModel(model).duration.default,
	);
	const [generateAudio, setGenerateAudio] = useState(true);
	// 首页 / /image-to-video / 首尾帧页共用这个 hook，提交时把页面记进 metadata.page
	// 好按页面分消耗；口径同付费墙事件（去语言前缀、模板页合并）
	const pathname = useLocalePathname();
	const pendingKey = pendingStateKey ?? PENDING_STATE_KEY;
	const [outputs, setOutputs] = useState(1);
	const [pendingTaskIds, setPendingTaskIds] = useState<string[]>([]);
	const [latestStartedBatchId, setLatestStartedBatchId] = useState<
		string | null
	>(null);
	/**
	 * 刷新 / 离开再回来后接手轮询的那一批里最新的一个。首页 hero 只按批次显示结果，
	 * 靠它把进行中的任务重新显示出来。单独一个值而不是复用 latestStartedBatchId：
	 * /image-to-video 用后者触发「滚到新卡片」，恢复时不该滚。
	 */
	const [resumedBatchId, setResumedBatchId] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [paywallReason, setPaywallReason] = useState<I2VPaywallReason | null>(
		null,
	);

	// Every task id tracked this session (started or resumed). Stops the resume
	// effect from re-adding a task that already settled or was dropped.
	const knownTaskIdsRef = useRef<Set<string>>(new Set());
	// Tasks resumed from history rather than started here — their failures are
	// silent, since the user isn't actively waiting on them.
	const resumedTaskIdsRef = useRef<Set<string>>(new Set());
	// Model per task, for failure reporting — /status doesn't return it
	const taskModelsRef = useRef<Map<string, string>>(new Map());
	const pollFailuresRef = useRef<Map<string, number>>(new Map());

	const { user } = useSession();
	const queryClient = useQueryClient();
	const {
		credits,
		getRemainingCredits,
		getRemainingCreditsWhenLoaded,
		hasUnlimitedCredits,
	} = useCredits();
	const { planTier, loading: purchasesLoading } = usePurchases();
	// 已经开着别的付费墙（比如又点了生成、积分不足）就不覆盖它的文案
	const schedulePostGenerationPaywall = usePostGenerationPaywall(() =>
		setPaywallReason((current) => current ?? "post-generation"),
	);

	const modelConfig = getI2VModel(model);
	useEffect(() => {
		if (!modelConfig.outputOptions.includes(outputs)) {
			setOutputs(modelConfig.outputOptions[0]);
		}
	}, [modelConfig, outputs]);
	const maxImages = getMaxI2VImages(model, mode, resolution);
	const filledImages = useMemo(
		() => inputImages.filter((image): image is I2VInputImage => !!image),
		[inputImages],
	);
	useEffect(() => {
		if (
			model === "fast-3.0" &&
			resolution === "1080p" &&
			filledImages.length > 1
		) {
			setResolutionState("720p");
		}
	}, [model, resolution, filledImages.length]);
	// 文生视频没有输入图可以决定画幅，比例总是可选
	const canPickAspectRatio =
		mode === "text" ||
		filledImages.length >= (modelConfig.aspectRatioMinImages ?? 0);
	const aspectRatio = canPickAspectRatio
		? clampI2VAspectRatio(model, aspectRatioState, mode)
		: "auto";
	useEffect(() => {
		if (!canPickAspectRatio) {
			setAspectRatio("auto");
		}
	}, [canPickAspectRatio]);

	/**
	 * 分辨率按档位切（480p 免费 / 720p Starter / 1080p+4K Pro），所有模型一视同仁。
	 * purchasesLoading 期间不锁，避免进页面闪一下锁图标。
	 */
	const lockedResolutions = useMemo(() => {
		if (purchasesLoading) {
			return new Set<string>();
		}
		return new Set<string>(
			modelConfig.resolutions.filter(
				(item) => !canUseVideoResolution(planTier, item),
			),
		);
	}, [purchasesLoading, planTier, modelConfig]);

	/** 切模型 / 恢复现场时可能落在锁定档，拉回第一个免费档，否则 UI 与实际不符 */
	useEffect(() => {
		if (!lockedResolutions.has(resolution)) {
			return;
		}
		const free = modelConfig.resolutions.find(
			(item) => !lockedResolutions.has(item),
		);
		if (free) {
			setResolutionState(free);
		}
	}, [lockedResolutions, resolution, modelConfig]);

	const referenceVideos = useMemo(
		() => referenceMedia.filter((item) => item.kind === "video"),
		[referenceMedia],
	);
	const referenceAudios = useMemo(
		() => referenceMedia.filter((item) => item.kind === "audio"),
		[referenceMedia],
	);
	const videoLimits = supportsI2VReferenceVideo(model, mode)
		? modelConfig.referenceVideo
		: undefined;
	const audioLimits = supportsI2VReferenceAudio(model, mode)
		? modelConfig.referenceAudio
		: undefined;
	// 只有视频计费，音频不计（2026-10-10 实测）
	const referenceVideoSeconds =
		videoLimits && referenceVideos.length
			? getBillableVideoSeconds(
					referenceVideos.reduce(
						(sum, video) => sum + video.durationSec,
						0,
					),
				)
			: 0;
	const isProcessingMedia = referenceMedia.some(
		(item) => !item.url || !item.durationSec,
	);
	const creditCost =
		getI2VCreditCost(model, resolution, duration, referenceVideoSeconds) *
		outputs;

	/**
	 * 必填的图齐了没：start 1 张、between 首尾都要、text 不要图；
	 * reference 默认至少 1 张，固定槽位时每个槽都得填（requiredReferenceSlots 只要前几个）
	 */
	const isMissingRequiredVideo =
		requireReferenceVideo &&
		mode === "reference" &&
		referenceVideos.length === 0;
	// 对口型档（OmniHuman）：音频必填，成片跟音频一样长
	const audioDrivesDuration =
		mode === "reference" && !!audioLimits?.drivesDuration;
	const isMissingRequiredAudio =
		audioDrivesDuration && referenceAudios.length === 0;
	const hasRequiredImages =
		mode === "text"
			? true
			: isMissingRequiredVideo || isMissingRequiredAudio
				? false
				: (requireReferenceVideo || audioDrivesDuration) &&
						mode === "reference"
					? filledImages.length > 0
					: mode === "reference" && !referenceSlots
						? filledImages.length > 0 || referenceMedia.length > 0
						: inputImages.length > 0 &&
							inputImages
								.slice(
									0,
									mode === "reference"
										? requiredReferenceSlots
										: undefined,
								)
								.every((image) => !!image);

	// 对口型档：音频截好（或换了一段）就把时长对齐音频，积分和服务端一样按向上取整的秒数算
	const referenceAudioTotalSeconds = referenceAudios.reduce(
		(sum, audio) => sum + audio.durationSec,
		0,
	);
	useEffect(() => {
		if (!audioDrivesDuration || referenceAudioTotalSeconds <= 0) {
			return;
		}
		setDuration(
			clampI2VDuration(
				model,
				getBillableVideoSeconds(referenceAudioTotalSeconds),
				mode,
			),
		);
	}, [audioDrivesDuration, mode, model, referenceAudioTotalSeconds]);

	// requireReferenceVideo 的页面：视频时长测出来（或换了视频）就把输出时长对齐原片
	const referenceVideoTotalSeconds = referenceVideos.reduce(
		(sum, video) => sum + video.durationSec,
		0,
	);
	useEffect(() => {
		if (
			!requireReferenceVideo ||
			mode !== "reference" ||
			referenceVideoTotalSeconds <= 0
		) {
			return;
		}
		setDuration(
			clampI2VDuration(
				model,
				Math.round(referenceVideoTotalSeconds),
				mode,
			),
		);
	}, [requireReferenceVideo, mode, model, referenceVideoTotalSeconds]);

	// 比例同理：reference 模式不给 auto，按第一段视频的宽高挑最接近的比例，
	// 免得竖屏原片出成 16:9。只读元数据，blob: 和 R2 地址都不需要 CORS
	const firstReferenceVideoSrc =
		referenceVideos[0]?.previewUrl ?? referenceVideos[0]?.url ?? null;
	useEffect(() => {
		if (
			!requireReferenceVideo ||
			mode !== "reference" ||
			!firstReferenceVideoSrc
		) {
			return;
		}
		let cancelled = false;
		const video = document.createElement("video");
		video.preload = "metadata";
		video.onloadedmetadata = () => {
			if (cancelled || !video.videoWidth || !video.videoHeight) {
				return;
			}
			const target = Math.log(video.videoWidth / video.videoHeight);
			let best: I2VAspectRatio | null = null;
			let bestGap = Number.POSITIVE_INFINITY;
			for (const ratio of getI2VAspectRatios(model, mode)) {
				const [w, h] = ratio.split(":").map(Number);
				if (!w || !h) {
					continue;
				}
				const gap = Math.abs(Math.log(w / h) - target);
				if (gap < bestGap) {
					best = ratio;
					bestGap = gap;
				}
			}
			if (best) {
				setAspectRatio(best);
			}
		};
		video.src = firstReferenceVideoSrc;
		return () => {
			cancelled = true;
			video.removeAttribute("src");
		};
	}, [requireReferenceVideo, mode, model, firstReferenceVideoSrc]);

	/** 切模型时把分辨率、比例、时长、图片数都夹回目标模型支持的范围 */
	const setModel = useCallback(
		(next: I2VModelId) => {
			setModelState(next);
			setResolutionState((previous) =>
				clampI2VResolution(next, previous),
			);
			setAspectRatio((previous) =>
				clampI2VAspectRatio(next, previous, mode),
			);
			setDuration((previous) => clampI2VDuration(next, previous, mode));
			setInputImages((previous) => {
				const limit = getMaxI2VImages(next, mode);
				if (previous.length <= limit) {
					return previous;
				}
				revokeAll(previous.slice(limit));
				return previous.slice(0, limit);
			});
			setReferenceMedia((previous) => {
				const dropped = previous.filter(
					(item) => !supportsMediaKind(next, mode, item.kind),
				);
				if (!dropped.length) {
					return previous;
				}
				revokeMedia(dropped);
				return previous.filter((item) => !dropped.includes(item));
			});
		},
		[mode],
	);

	/**
	 * 切 tab = 换一种输入范式，图片语义完全不同（首帧 / 首尾帧 / 参考图），
	 * 所以图片不迁移、直接清空重来。但模型和分辨率跟着走：模型只在目标 tab
	 * 用不了时才回落到该 tab 的默认（见 carryI2VModelToMode），分辨率保留后
	 * 夹到新模型支持的档。模型没换就别动时长，换了才取新模型的默认值。
	 */
	const setMode = useCallback(
		(next: I2VMode) => {
			const nextModel = carryI2VModelToMode(model, next);
			setModeState(next);
			setModelState(nextModel);
			setResolutionState((previous) =>
				clampI2VResolution(nextModel, previous),
			);
			setAspectRatio((previous) =>
				clampI2VAspectRatio(nextModel, previous, next),
			);
			if (nextModel !== model) {
				setDuration(getI2VModel(nextModel).duration.default);
			}
			setInputImages((previous) => {
				revokeAll(previous);
				return emptySlotsFor(next, referenceSlots);
			});
			setReferenceMedia((previous) => {
				revokeMedia(previous);
				return [];
			});
		},
		[model, referenceSlots],
	);

	const setResolution = useCallback((next: I2VResolution) => {
		setResolutionState(next);
	}, []);

	/** 固定槽位（start / between）的赋值与清空 */
	const setImageAt = useCallback(
		(index: number, image: I2VInputImage | null, onlyIfEmpty = false) => {
			setInputImages((previous) =>
				previous.map((current, i) => {
					// 默认图按最新 state 判空，避免同一轮 effect 覆盖刚恢复的历史照片。
					if (i !== index || (onlyIfEmpty && current)) {
						return current;
					}
					if (current) {
						revokeIfBlob(current.previewUrl);
					}
					return image;
				}),
			);
		},
		[],
	);

	/** reference tab：追加本地文件，超出上限的丢弃并返回丢了几张 */
	const addImageFiles = useCallback(
		(files: File[]) => {
			let skipped = 0;
			setInputImages((previous) => {
				const room = maxImages - previous.length;
				skipped = Math.max(0, files.length - room);
				return [
					...previous,
					...files.slice(0, room).map((file) => ({
						previewUrl: URL.createObjectURL(file),
						file,
					})),
				];
			});
			return skipped;
		},
		[maxImages],
	);

	/** reference tab：追加素材库里已经在 R2 上的图 */
	const addExistingImages = useCallback(
		(urls: string[]) => {
			setInputImages((previous) => [
				...previous,
				...urls
					.slice(0, maxImages - previous.length)
					.map((url) => ({ previewUrl: url })),
			]);
		},
		[maxImages],
	);

	const removeImage = useCallback(
		(index: number) => {
			if (mode !== "reference") {
				setImageAt(index, null);
				return;
			}
			setInputImages((previous) => {
				const target = previous[index];
				if (target) {
					revokeIfBlob(target.previewUrl);
				}
				return previous.filter((_, i) => i !== index);
			});
		},
		[mode, setImageAt],
	);

	/**
	 * reference tab 删图后点 Undo：放回原位。本地图的 blob 删的时候已经回收了，
	 * 用 file 重新生成一个预览地址；素材库的图直接用原地址
	 */
	const restoreImageAt = useCallback(
		(index: number, image: I2VInputImage) => {
			setInputImages((previous) =>
				previous.length >= maxImages
					? previous
					: [
							...previous.slice(0, index),
							image.file
								? {
										...image,
										previewUrl: URL.createObjectURL(
											image.file,
										),
									}
								: image,
							...previous.slice(index),
						],
			);
		},
		[maxImages],
	);

	/** 裁剪对话框确认后就地替换那一张 */
	const replaceImage = useCallback((index: number, file: File) => {
		setInputImages((previous) =>
			previous.map((image, i) => {
				if (i !== index || !image) {
					return image;
				}
				revokeIfBlob(image.previewUrl);
				return { previewUrl: URL.createObjectURL(file), file };
			}),
		);
	}, []);

	const updateMedia = useCallback(
		(id: string, patch: Partial<I2VReferenceMedia>) => {
			setReferenceMedia((previous) =>
				previous.map((item) =>
					item.id === id ? { ...item, ...patch } : item,
				),
			);
		},
		[],
	);

	const removeReferenceMedia = useCallback((id: string) => {
		setReferenceMedia((previous) => {
			revokeMedia(previous.filter((item) => item.id === id));
			return previous.filter((item) => item.id !== id);
		});
	}, []);

	/**
	 * reference tab 加参考视频 / 音频。当前模型收不了其中某一类时先切到 fallbackModels
	 * 里第一个都收的（老公定：Grok 下传视频自动切 Seedance 2.0 Mini），返回切到哪档好提示
	 * 用户。超过合计时长的截掉后面（老公定），超出段数的丢掉。逐段串行转码，并行会抢编码器。
	 */
	const addReferenceMedia = useCallback(
		(
			files: { file: File; kind: I2VReferenceMediaKind }[],
			fallbackModels: readonly I2VModelId[] = ["seedance-2-mini"],
			/** 合计时长再压低一点（AI Dance 的用户视频要同时过 Mini 和 Kling motion-control，取 10s） */
			maxTotalSeconds = Number.POSITIVE_INFINITY,
			/** 单段最短再抬高一点（同上，Kling motion-control 要 3s，Mini 只要 2s） */
			minSeconds = 0,
		): { switchedTo: I2VModelId | null; skipped: number } => {
			// 处理中的条目还没有时长，这时再加一批会多拿一份合计时长额度
			if (isProcessingMedia) {
				toast.info(
					"Your file is still processing. Please wait a moment.",
				);
				return { switchedTo: null, skipped: 0 };
			}
			// 已有的和新加的每一类都得收，切过去才不会把已有的清掉
			const kinds = new Set([
				...referenceMedia.map((item) => item.kind),
				...files.map((item) => item.kind),
			]);
			const supportsAll = (id: I2VModelId) =>
				Array.from(kinds).every((kind) =>
					supportsMediaKind(id, mode, kind),
				);
			const target = supportsAll(model)
				? model
				: fallbackModels.find(supportsAll);
			if (!target) {
				return { switchedTo: null, skipped: files.length };
			}
			if (target !== model) {
				setModel(target);
			}

			const counts = { video: 0, audio: 0 };
			const usedSeconds = { video: 0, audio: 0 };
			for (const item of referenceMedia) {
				counts[item.kind]++;
				usedSeconds[item.kind] += item.durationSec;
			}
			const accepted = files.filter(({ kind }) => {
				const limits = mediaLimits(target, kind);
				if (!limits || counts[kind] >= limits.maxCount) {
					return false;
				}
				counts[kind]++;
				return true;
			});
			const entries: I2VReferenceMedia[] = accepted.map(({ kind }) => ({
				id: crypto.randomUUID(),
				kind,
				previewUrl: null,
				durationSec: 0,
				url: null,
				progress: 0,
			}));
			setReferenceMedia((previous) => [...previous, ...entries]);

			void (async () => {
				const normalizers = await import(
					"../lib/normalize-reference-media"
				).catch((error: unknown) => {
					console.error("Failed to load the media converter:", error);
					return null;
				});
				if (!normalizers) {
					// 断网或发版后旧 chunk 失效，不清掉的话这几格会一直转圈、卡住生成
					const ids = new Set(entries.map((entry) => entry.id));
					setReferenceMedia((previous) =>
						previous.filter((item) => !ids.has(item.id)),
					);
					toast.error(
						"Couldn't prepare your file. Please refresh the page and try again.",
					);
					return;
				}
				const { normalizeReferenceAudio, normalizeReferenceVideo } =
					normalizers;
				for (let index = 0; index < accepted.length; index++) {
					const { file, kind } = accepted[index];
					const { id } = entries[index];
					const modelLimits = mediaLimits(target, kind);
					if (!modelLimits) {
						continue;
					}
					const limits = {
						...modelLimits,
						minSeconds: Math.max(
							modelLimits.minSeconds,
							minSeconds,
						),
						maxTotalSeconds: Math.min(
							modelLimits.maxTotalSeconds,
							maxTotalSeconds,
						),
					};
					const budget = limits.maxTotalSeconds - usedSeconds[kind];
					try {
						if (budget < limits.minSeconds) {
							toast.error(
								`This model takes up to ${limits.maxTotalSeconds}s of reference ${kind} in total.`,
							);
							setReferenceMedia((previous) =>
								previous.filter((item) => item.id !== id),
							);
							continue;
						}
						const normalize =
							kind === "video"
								? normalizeReferenceVideo
								: normalizeReferenceAudio;
						const result = await normalize(file, {
							minSeconds: limits.minSeconds,
							maxSeconds: budget,
							onProgress: (progress) =>
								updateMedia(id, { progress: progress * 0.9 }),
						});
						updateMedia(id, {
							previewUrl: URL.createObjectURL(result.file),
							durationSec: result.durationSec,
							progress: 0.9,
						});
						const url = await uploadReferenceMediaToR2Directly(
							result.file,
						);
						// 传成功才占额度，传失败这段被删掉，秒数要留给后面的
						usedSeconds[kind] += result.durationSec;
						updateMedia(id, { url, progress: 1 });
						if (result.trimmed) {
							toast.info(
								`Only the first ${Math.round(result.durationSec)}s of your ${kind} is used. Reference ${kind} can be ${limits.maxTotalSeconds}s in total.`,
							);
						}
					} catch (error) {
						console.error(
							`Failed to prepare reference ${kind}:`,
							error,
						);
						setReferenceMedia((previous) => {
							revokeMedia(
								previous.filter((item) => item.id === id),
							);
							return previous.filter((item) => item.id !== id);
						});
						toast.error(
							referenceMediaErrorMessage(
								error,
								kind,
								limits.minSeconds,
							),
						);
					}
				}
			})();

			return {
				switchedTo: target !== model ? target : null,
				skipped: files.length - accepted.length,
			};
		},
		[isProcessingMedia, mode, model, referenceMedia, setModel, updateMedia],
	);

	/**
	 * 对口型页（/ai-music-video-generator）的歌：从 startSec 起截 seconds 秒转 wav 上传，
	 * 替换掉之前那段（只收 1 段）。截取条每松手一次调一次
	 */
	// 最近一次截取的条目 id：连着拖几次时，前几段转完就不用再上传了
	const drivingAudioIdRef = useRef<string | null>(null);
	const setDrivingAudio = useCallback(
		(file: Blob, startSec: number, seconds: number) => {
			const id = crypto.randomUUID();
			drivingAudioIdRef.current = id;
			setReferenceMedia((previous) => {
				revokeMedia(previous.filter((item) => item.kind === "audio"));
				return [
					...previous.filter((item) => item.kind !== "audio"),
					{
						id,
						kind: "audio",
						previewUrl: null,
						// 先用截取条上的秒数，积分松手就跟着变；url 没回来之前仍算处理中，转完用实际时长覆盖
						durationSec: seconds,
						url: null,
						progress: 0,
					},
				];
			});
			const drop = () =>
				setReferenceMedia((previous) => {
					revokeMedia(previous.filter((item) => item.id === id));
					return previous.filter((item) => item.id !== id);
				});
			void (async () => {
				const limits = audioLimits;
				try {
					if (!limits) {
						throw new Error("This model does not take audio");
					}
					const { normalizeReferenceAudio } = await import(
						"../lib/normalize-reference-media"
					);
					const result = await normalizeReferenceAudio(
						new File([file], "song", { type: file.type }),
						{
							minSeconds: limits.minSeconds,
							maxSeconds: Math.min(
								seconds,
								limits.maxTotalSeconds,
							),
							startSeconds: startSec,
							sampleRate: 44100,
							onProgress: (progress) =>
								updateMedia(id, { progress: progress * 0.9 }),
						},
					);
					if (drivingAudioIdRef.current !== id) {
						return;
					}
					updateMedia(id, {
						previewUrl: URL.createObjectURL(result.file),
						durationSec: result.durationSec,
						progress: 0.9,
					});
					const url = await uploadReferenceMediaToR2Directly(
						result.file,
					);
					updateMedia(id, { url, progress: 1 });
				} catch (error) {
					console.error("Failed to prepare the song:", error);
					drop();
					if (drivingAudioIdRef.current !== id) {
						return;
					}
					toast.error(
						referenceMediaErrorMessage(
							error,
							"audio",
							limits?.minSeconds ?? 0,
						),
					);
				}
			})();
		},
		[audioLimits, updateMedia],
	);

	/** 历史 / 登录前现场里已经在 R2 上的视频和音频；时长缺了就现量 */
	const restoreReferenceMedia = useCallback(
		(
			items: {
				kind: I2VReferenceMediaKind;
				url: string;
				durationSec: number;
			}[],
		) => {
			const entries = items.map((item) => ({
				id: crypto.randomUUID(),
				kind: item.kind,
				previewUrl: item.url,
				durationSec: item.durationSec,
				url: item.url,
				progress: 1,
			}));
			setReferenceMedia((previous) => {
				revokeMedia(previous);
				return entries;
			});
			for (const entry of entries) {
				void loadMediaDuration(entry.url, entry.kind).then(
					(seconds) => {
						if (seconds) {
							updateMedia(entry.id, { durationSec: seconds });
							return;
						}
						// 量不出时长（文件没了、断网）又没存过秒数，这格会一直「处理中」、卡住生成
						if (!entry.durationSec) {
							removeReferenceMedia(entry.id);
							toast.error(
								`Your previous reference ${entry.kind} is no longer available. Please upload it again.`,
							);
						}
					},
				);
			}
		},
		[removeReferenceMedia, updateMedia],
	);

	/** 样片一键回填：同一轮设置 mode 和 model，避免 setModel 读到旧 mode 后清掉参考图。 */
	const setExample = useCallback(
		(example: {
			mode: I2VMode;
			model: I2VModelId;
			prompt: string;
			images: string[];
			aspectRatio: I2VAspectRatio;
			/** 不传就保留用户当前选的时长（样片没有固定时长，prompt 模板有） */
			duration?: number;
			referenceMedia?: {
				kind: I2VReferenceMediaKind;
				url: string;
				durationSec: number;
			}[];
		}) => {
			const slots = emptySlotsFor(example.mode, referenceSlots);
			const images = slots.length
				? slots.map((_, index) =>
						example.images[index]
							? { previewUrl: example.images[index] }
							: null,
					)
				: example.images
						.slice(0, getMaxI2VImages(example.model, example.mode))
						.map((url) => ({ previewUrl: url }));
			setModeState(example.mode);
			setModelState(example.model);
			setInputImages((previous) => {
				revokeAll(previous);
				return images;
			});
			restoreReferenceMedia(example.referenceMedia ?? []);
			setPrompt(example.prompt);
			setResolutionState((previous) =>
				clampI2VResolution(example.model, previous),
			);
			setAspectRatio(
				clampI2VAspectRatio(
					example.model,
					example.aspectRatio,
					example.mode,
				),
			);
			setDuration((previous) =>
				clampI2VDuration(
					example.model,
					example.duration ?? previous,
					example.mode,
				),
			);
		},
		[referenceSlots, restoreReferenceMedia],
	);

	const clearImages = useCallback(() => {
		setInputImages((previous) => {
			revokeAll(previous);
			return emptySlotsFor(mode, referenceSlots);
		});
	}, [mode, referenceSlots]);

	/** Load a past generation back into the form (Reprompt / Regenerate) */
	const applyHistoryItem = useCallback(
		(item: VideoHistoryItem) => {
			const metadata = (item.metadata ?? {}) as I2VRowMetadata;
			// 文生视频的行 inputImageUrl 是空串，别把它当成一张图回填
			const urls = (
				item.inputImageUrls?.length
					? item.inputImageUrls
					: [item.inputImageUrl]
			).filter(Boolean);
			const nextMode = toSupportedMode(
				resolveHistoryRowMode(item.metadata, urls.length),
				allowTextMode,
			);
			const historyModel = resolveHistoryRowModelId(item.metadata);
			const nextModel = historyModel
				? carryI2VModelToMode(historyModel, nextMode)
				: DEFAULT_I2V_MODEL[nextMode];
			// 有固定槽位（start / between / 预设 reference）按槽回填，否则按列表回填
			const slots = emptySlotsFor(nextMode, referenceSlots);
			const restored: (I2VInputImage | null)[] = slots.length
				? slots.map((_, index) =>
						urls[index] ? { previewUrl: urls[index] } : null,
					)
				: urls
						.slice(0, getMaxI2VImages(nextModel, nextMode))
						.map((url) => ({ previewUrl: url }));

			setModeState(nextMode);
			setModelState(nextModel);
			setInputImages((previous) => {
				revokeAll(previous);
				return restored;
			});
			// 视频只存了合计秒数，先平均分给每段让积分立刻显示；音频不计费，时长现量
			const videoUrls = supportsI2VReferenceVideo(nextModel, nextMode)
				? (metadata.referenceVideoUrls ?? [])
				: [];
			const audioUrls = supportsI2VReferenceAudio(nextModel, nextMode)
				? (metadata.referenceAudioUrls ?? [])
				: [];
			restoreReferenceMedia([
				...videoUrls.map((url) => ({
					kind: "video" as const,
					url,
					durationSec:
						(metadata.referenceVideoSeconds ?? 0) /
						videoUrls.length,
				})),
				...audioUrls.map((url) => ({
					kind: "audio" as const,
					url,
					durationSec: 0,
				})),
			]);
			setPrompt(item.prompt);
			setResolutionState(
				clampI2VResolution(nextModel, item.resolution as I2VResolution),
			);
			setAspectRatio(
				clampI2VAspectRatio(
					nextModel,
					(metadata.aspectRatio ??
						DEFAULT_I2V_ASPECT_RATIO) as I2VAspectRatio,
					nextMode,
				),
			);
			setDuration(clampI2VDuration(nextModel, item.duration, nextMode));
			setGenerateAudio(metadata.generateAudio ?? true);
		},
		[allowTextMode, referenceSlots, restoreReferenceMedia],
	);

	// Start generation
	const generateMutation = useMutation({
		mutationFn: async ({
			imageUrls,
			referenceVideoUrls,
			referenceAudioUrls,
			batchId,
		}: {
			imageUrls: string[];
			referenceVideoUrls: string[];
			referenceAudioUrls: string[];
			batchId: string;
		}) => {
			const sentAspectRatio = clampI2VAspectRatio(
				model,
				aspectRatio,
				mode,
			);
			const response = await apiClient["image-to-video"].generate.$post({
				json: {
					mode,
					model,
					prompt: prompt.trim(),
					imageUrls,
					...(referenceVideoUrls.length && { referenceVideoUrls }),
					...(referenceAudioUrls.length && { referenceAudioUrls }),
					resolution,
					...(sentAspectRatio && { aspectRatio: sentAspectRatio }),
					duration,
					...(model === "seedance-2.5" && { generateAudio }),
					batchId,
					sourcePage: normalizePage(pathname),
				},
			});

			if (response.status === 402) {
				throw new Error("INSUFFICIENT_CREDITS");
			}
			if (!response.ok) {
				const errorData = (await response.json().catch(() => ({}))) as {
					error?: string;
					reason?: string;
					message?: string;
					retryAfterMinutes?: number;
				};
				if (errorData.error === "INVALID_INPUT_IMAGE") {
					throw new Error(errorData.message, {
						cause: "INVALID_INPUT_IMAGE",
					});
				}
				if (errorData.error === "PREMIUM_RESOLUTION") {
					throw new Error("PREMIUM_RESOLUTION");
				}
				// 这个人最近一小时被 Grok 拒太多次，后端先停他的 Grok（见 grok-cooldown.ts）
				if (errorData.error === "GROK_COOLDOWN") {
					throw new Error("GROK_COOLDOWN", {
						cause: errorData.retryAfterMinutes,
					});
				}
				// 审核只在明确判拦时返回这个 code（通道挂掉是 fail open）。
				// reason 区分拦的是提示词还是输入图，图被拦改提示词没用
				if (errorData.error === "CONTENT_MODERATION_BLOCKED") {
					throw new Error(
						errorData.reason === "image"
							? "CONTENT_MODERATION_IMAGE_BLOCKED"
							: "CONTENT_MODERATION_BLOCKED",
					);
				}
				throw new Error("Failed to start video generation");
			}

			return (await response.json()) as {
				taskId: string;
				status: string;
				creditCost: number;
			};
		},
		onSuccess: (data, { batchId }) => {
			knownTaskIdsRef.current.add(data.taskId);
			taskModelsRef.current.set(data.taskId, model);
			setLatestStartedBatchId(batchId);
			setPendingTaskIds((previous) => [...previous, data.taskId]);
			// 立刻刷新历史，让 GENERATING 卡片出现在右侧
			queryClient.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
		},
		onError: (error) => {
			console.error(error);
			reportGenerationFailure("image-to-video", model, error);
			const message = error instanceof Error ? error.message : "";
			if (message === "INSUFFICIENT_CREDITS") {
				setPaywallReason("insufficient-credits");
				return;
			}
			if (message === "PREMIUM_RESOLUTION") {
				setPaywallReason("video-resolution");
				return;
			}
			if (message === "GROK_COOLDOWN") {
				toast.error(
					`Too many of your recent Grok videos were blocked by its safety filter. Try again in ${(error as Error).cause ?? 60} min, or switch to another model.`,
				);
				return;
			}
			if (message === "CONTENT_MODERATION_BLOCKED") {
				toast.error(
					"This prompt doesn't meet our content guidelines. Please edit it and try again.",
				);
				return;
			}
			if (message === "CONTENT_MODERATION_IMAGE_BLOCKED") {
				toast.error(
					"This image doesn't meet our content guidelines. Please try a different image.",
				);
				return;
			}
			if (
				error instanceof Error &&
				error.cause === "INVALID_INPUT_IMAGE"
			) {
				toast.error(error.message);
				return;
			}
			toast.error("Failed to start generation. Please try again.");
		},
	});

	// Poll every in-flight task. There is no webhook — a task only finalizes
	// (result pulled, stored on R2, credits settled) when /status is called for
	// it, so with multiple outputs every id has to be polled, not just one.
	const { data: taskStatuses } = useQuery({
		queryKey: ["imageToVideoTasks", pendingTaskIds],
		queryFn: async () => {
			// allSettled so one broken task can't kill polling for the others
			const results = await Promise.allSettled(
				pendingTaskIds.map(async (taskId) => {
					const response = await apiClient["image-to-video"].status[
						":taskId"
					].$get({ param: { taskId } });

					if (!response.ok) {
						throw new Error(
							response.status === 404
								? "not found"
								: "Failed to get task status",
						);
					}

					return (await response.json()) as {
						taskId: string;
						status: string;
						videoUrl?: string;
						errorMessage?: string;
					};
				}),
			);

			return results.map((result, index) =>
				result.status === "fulfilled"
					? result.value
					: {
							taskId: pendingTaskIds[index],
							status: "POLL_ERROR" as const,
							errorMessage:
								result.reason instanceof Error
									? result.reason.message
									: "Failed to get task status",
						},
			);
		},
		enabled: pendingTaskIds.length > 0,
		refetchInterval: 3000,
		// 没有 webhook，任务只在 /status 被调用时才结算（转存 R2 + 扣积分）。
		// 默认后台标签页会暂停 refetchInterval，用户切走几分钟回来才结算，
		// 期间页面一直显示 Generating。后台也照常轮询。
		refetchIntervalInBackground: true,
	});

	useEffect(() => {
		if (!taskStatuses?.length) {
			return;
		}

		const settled = taskStatuses.filter(
			(task) => task.status === "SUCCESS" || task.status === "FAILED",
		);

		// Poll errors only drop their own task, and only after a few
		// consecutive failures — a transient network blip shouldn't kill it
		const dropped: string[] = [];
		for (const task of taskStatuses) {
			if (task.status === "POLL_ERROR") {
				const failures =
					(pollFailuresRef.current.get(task.taskId) ?? 0) + 1;
				pollFailuresRef.current.set(task.taskId, failures);
				if (
					failures >= MAX_POLL_FAILURES ||
					task.errorMessage?.includes("not found")
				) {
					dropped.push(task.taskId);
				}
			} else {
				pollFailuresRef.current.delete(task.taskId);
			}
		}

		if (!settled.length && !dropped.length) {
			return;
		}

		const removedIdList = [
			...settled.map((task) => task.taskId),
			...dropped,
		];
		const removedIds = new Set(removedIdList);
		setPendingTaskIds((previous) =>
			previous.filter((id) => !removedIds.has(id)),
		);
		for (const id of removedIdList) {
			pollFailuresRef.current.delete(id);
		}

		if (settled.length) {
			queryClient.invalidateQueries({ queryKey: ["credits"] });
			queryClient.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
		}

		// 恢复轮询接手的任务不算：用户刷新回来时没在等它
		if (
			settled.some(
				(task) =>
					task.status === "SUCCESS" &&
					!resumedTaskIdsRef.current.has(task.taskId),
			)
		) {
			schedulePostGenerationPaywall();
		}

		// Resumed tasks fail silently — the user isn't actively waiting on
		// them, and the history card already shows the failure
		for (const task of settled) {
			if (task.status === "FAILED") {
				reportGenerationFailure(
					"image-to-video",
					taskModelsRef.current.get(task.taskId),
					task.errorMessage || "Generation failed",
				);
			}
			if (
				task.status === "FAILED" &&
				!resumedTaskIdsRef.current.has(task.taskId)
			) {
				toast.error(
					task.errorMessage || "Generation failed. Please try again.",
				);
			}
		}
		for (const id of dropped) {
			console.error(
				"Stopped polling image-to-video task after errors:",
				id,
			);
			reportGenerationFailure(
				"image-to-video",
				taskModelsRef.current.get(id),
				"Lost connection to task",
			);
			if (!resumedTaskIdsRef.current.has(id)) {
				toast.error("Lost connection to task. Please try again.");
			}
		}
	}, [taskStatuses, queryClient, schedulePostGenerationPaywall]);

	// History
	const {
		data: historyData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isHistoryLoading,
	} = useInfiniteQuery({
		// page size 必须进 key：图片页用同一前缀但 limit 不同，共用缓存会让
		// 第 2 页按错误的 limit 算 offset
		queryKey: historyQueryKey(HISTORY_PAGE_SIZE, historyFilter),
		enabled: Boolean(user),
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.generations.history.$get({
				query: {
					page: String(pageParam),
					limit: String(HISTORY_PAGE_SIZE),
					filter: historyFilter,
				},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch history");
			}
			return await response.json();
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			if (lastPage.pagination.page < lastPage.pagination.totalPages) {
				return lastPage.pagination.page + 1;
			}
			return undefined;
		},
	});

	const history: GenerationHistoryItem[] =
		historyData?.pages.flatMap(
			(page) => page.data as GenerationHistoryItem[],
		) ?? [];

	/**
	 * Resume polling for every unsettled task after a reload. 历史 feed 是两条
	 * 视频线合并的，只接手本工具的行——老 ai-video 的行由 sweep-generations cron 收尾。
	 */
	useEffect(() => {
		const resumable = history.filter(
			(item): item is { type: "video" } & VideoHistoryItem =>
				item.type === "video" &&
				(item.status === "GENERATING" ||
					item.status === "PROCESSING") &&
				isI2VHistoryRow(item.metadata) &&
				!knownTaskIdsRef.current.has(item.id),
		);
		if (!resumable.length) {
			return;
		}
		for (const item of resumable) {
			knownTaskIdsRef.current.add(item.id);
			resumedTaskIdsRef.current.add(item.id);
			taskModelsRef.current.set(
				item.id,
				item.metadata?.modelId ?? item.model,
			);
		}
		setPendingTaskIds((previous) => [
			...previous,
			...resumable.map((item) => item.id),
		]);
		// history 按时间倒序，第一个带 batchId 的就是最新一批
		const newestResumed = resumable.find(
			(item) => item.type === "video" && item.batchId,
		);
		const batchId =
			newestResumed?.type === "video" ? newestResumed.batchId : null;
		if (batchId) {
			setResumedBatchId((previous) => previous ?? batchId);
		}
	}, [history]);
	// 在生图页出图后切过来的，History 里还在转的图片也接着查
	useResumeHistoryTasks(history, "image");

	/**
	 * 弹登录前把现场存进 IndexedDB。Google / magic link 是整页跳转，React state
	 * 和 blob: 地址都活不过去。
	 */
	const requestLogin = useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}
		void saveBlobPendingState<PendingI2VState>(pendingKey, {
			mode,
			model,
			images: inputImages.map((image) => {
				if (!image) {
					return { kind: "empty" } as const;
				}
				return image.file
					? ({
							kind: "file",
							blob: image.file,
							name: image.file.name,
						} as const)
					: ({ kind: "url", url: image.previewUrl } as const);
			}),
			prompt,
			resolution,
			aspectRatio,
			duration,
			outputs,
			generateAudio,
			media: referenceMedia.flatMap((item) =>
				item.url && item.durationSec
					? [
							{
								kind: item.kind,
								url: item.url,
								durationSec: item.durationSec,
							},
						]
					: [],
			),
		});
		window.dispatchEvent(
			new CustomEvent("show-login", {
				detail: {
					redirectTo:
						window.location.pathname + window.location.search,
				},
			}),
		);
	}, [
		pendingKey,
		mode,
		model,
		inputImages,
		prompt,
		resolution,
		aspectRatio,
		duration,
		outputs,
		generateAudio,
		referenceMedia,
	]);

	// 登录回来后恢复现场，只做一次。takeBlobPendingState 读完即删，也会丢掉过期的。
	const hasRestoredRef = useRef(false);
	useEffect(() => {
		if (hasRestoredRef.current || !user) {
			return;
		}
		hasRestoredRef.current = true;

		takeBlobPendingState<PendingI2VState>(pendingKey, {
			maxAgeMs: PENDING_STATE_TTL,
		})
			.then((state) => {
				if (!state) {
					return;
				}
				const nextMode = toSupportedMode(state.mode, allowTextMode);
				const nextModel = isI2VModelId(state.model)
					? carryI2VModelToMode(state.model, nextMode)
					: DEFAULT_I2V_MODEL[nextMode];

				const images = state.images
					.slice(0, getMaxI2VImages(nextModel, nextMode))
					.map((image) => {
						if (image.kind === "empty") {
							return null;
						}
						if (image.kind === "url") {
							return { previewUrl: image.url };
						}
						const file = new File([image.blob], image.name, {
							type: image.blob.type,
						});
						return { previewUrl: URL.createObjectURL(file), file };
					});

				setModeState(nextMode);
				setModelState(nextModel);
				setInputImages(
					images.length
						? images
						: emptySlotsFor(nextMode, referenceSlots),
				);
				restoreReferenceMedia(
					(state.media ?? []).filter((item) =>
						supportsMediaKind(nextModel, nextMode, item.kind),
					),
				);
				setPrompt(
					state.prompt.slice(
						0,
						getI2VPromptMaxLength(nextModel, nextMode),
					),
				);
				setResolutionState(
					clampI2VResolution(
						nextModel,
						state.resolution as I2VResolution,
					),
				);
				setAspectRatio(
					clampI2VAspectRatio(
						nextModel,
						state.aspectRatio as I2VAspectRatio,
						nextMode,
					),
				);
				setDuration(
					clampI2VDuration(nextModel, state.duration, nextMode),
				);
				setGenerateAudio(state.generateAudio ?? true);
				if (
					getI2VModel(nextModel).outputOptions.includes(state.outputs)
				) {
					setOutputs(state.outputs);
				}
			})
			// 存下来的记录会跨版本存活，改了结构时旧记录不该把页面搞崩
			.catch((error) => {
				console.error("Failed to restore image-to-video state:", error);
			});
	}, [
		user,
		allowTextMode,
		referenceSlots,
		pendingKey,
		restoreReferenceMedia,
	]);

	const isPending =
		isUploading || generateMutation.isPending || pendingTaskIds.length > 0;

	const handleGenerate = useCallback(async () => {
		if (isPending) {
			return;
		}

		if (!user) {
			requestLogin();
			return;
		}

		if (!hasRequiredImages) {
			toast.error(
				isMissingRequiredVideo
					? "Please upload the video you want to edit"
					: isMissingRequiredAudio
						? "Please add a song"
						: mode === "between"
							? "Please upload both the start and the end frame"
							: "Please upload an image",
			);
			return;
		}

		if (isProcessingMedia) {
			toast.info("Your file is still processing. Please wait a moment.");
			return;
		}
		// 段数和合计时长加文件时按当时的模型截过，这里管的是之后又换了档
		if (videoLimits && referenceVideos.length) {
			if (
				referenceVideos.length > videoLimits.maxCount ||
				referenceVideoSeconds > videoLimits.maxTotalSeconds
			) {
				toast.error(
					`This model takes up to ${videoLimits.maxCount} videos, ${videoLimits.maxTotalSeconds}s in total. Please remove a video.`,
				);
				return;
			}
			if (
				videoLimits.maxCombinedSeconds &&
				referenceVideoSeconds + duration >
					videoLimits.maxCombinedSeconds
			) {
				toast.error(
					`Reference video plus output can be at most ${videoLimits.maxCombinedSeconds}s on this model. Please shorten the duration.`,
				);
				return;
			}
		}
		if (audioLimits && referenceAudios.length) {
			const audioSeconds = referenceAudios.reduce(
				(sum, audio) => sum + audio.durationSec,
				0,
			);
			if (
				referenceAudios.length > audioLimits.maxCount ||
				audioSeconds > audioLimits.maxTotalSeconds + 0.05
			) {
				toast.error(
					`This model takes up to ${audioLimits.maxCount} audio clips, ${audioLimits.maxTotalSeconds}s in total. Please remove one.`,
				);
				return;
			}
			if (
				audioLimits.needsVisual &&
				!referenceVideos.length &&
				!filledImages.length
			) {
				toast.error(
					"This model needs an image or video along with the audio.",
				);
				return;
			}
		}

		if (lockedResolutions.has(resolution)) {
			setPaywallReason("video-resolution");
			return;
		}

		const remainingCredits =
			await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
		// 上一次点击还在等积分，忽略这次，免得生成两次
		if (remainingCredits === null) return;
		if (remainingCredits < creditCost) {
			setPaywallReason("insufficient-credits");
			return;
		}

		const submittedImages =
			referenceVideoImageSlots && videoLimits && referenceVideos.length
				? filledImages.slice(0, referenceVideoImageSlots)
				: filledImages;
		setIsUploading(true);
		let imageUrls: string[];
		try {
			// 素材库选的图已经在 R2 上，只上传本地新选的，顺序保持和 UI 一致
			imageUrls = await Promise.all(
				submittedImages.map(async (image) => {
					if (!image.file) {
						return image.previewUrl;
					}
					if (image.uploadedUrl) {
						return image.uploadedUrl;
					}
					const compressed = await compressImage(image.file);
					return uploadImageToR2Directly(compressed);
				}),
			);
			// 记到槽位上；换图、重新裁剪会换成新对象，自然重新上传
			const uploaded = new Map<File, string>();
			submittedImages.forEach((image, index) => {
				if (image.file && !image.uploadedUrl) {
					uploaded.set(image.file, imageUrls[index]);
				}
			});
			if (uploaded.size > 0) {
				setInputImages((previous) =>
					previous.map((image) => {
						const uploadedUrl =
							image?.file && uploaded.get(image.file);
						return image && uploadedUrl
							? { ...image, uploadedUrl }
							: image;
					}),
				);
			}
		} catch (error) {
			console.error("Failed to upload image:", error);
			toast.error("Failed to upload image. Please try again.");
			setIsUploading(false);
			return;
		}
		setIsUploading(false);

		// 服务端会把越界的图居中裁掉，先告诉用户，不然成片少一截会以为出错了
		const range = modelConfig.inputAspectRange;
		if (range) {
			const ratios = await Promise.all(
				submittedImages.map((image) =>
					loadAspectRatio(image.previewUrl),
				),
			);
			const tooTall = ratios.some((r) => r !== null && r < range[0]);
			const tooWide = ratios.some((r) => r !== null && r > range[1]);
			if (tooTall || tooWide) {
				toast.info(
					tooTall
						? "Your image is too tall for this model, so it's center-cropped to 9:16."
						: "Your image is too wide for this model, so it's center-cropped to 16:9.",
				);
			}
		}

		// One request per output — the API generates a single clip per call.
		// They share a batchId so history shows them as one entry.
		const batchId = crypto.randomUUID();
		const referenceVideoUrls = videoLimits
			? referenceVideos.flatMap((video) => (video.url ? [video.url] : []))
			: [];
		const referenceAudioUrls = audioLimits
			? referenceAudios.flatMap((audio) => (audio.url ? [audio.url] : []))
			: [];
		for (let i = 0; i < outputs; i++) {
			generateMutation.mutate({
				imageUrls,
				referenceVideoUrls,
				referenceAudioUrls,
				batchId,
			});
		}
	}, [
		getRemainingCreditsWhenLoaded,
		isPending,
		user,
		requestLogin,
		mode,
		hasRequiredImages,
		isMissingRequiredVideo,
		isMissingRequiredAudio,
		filledImages,
		referenceVideoImageSlots,
		isProcessingMedia,
		videoLimits,
		audioLimits,
		referenceVideos,
		referenceAudios,
		referenceVideoSeconds,
		duration,
		lockedResolutions,
		resolution,
		outputs,
		modelConfig,
		creditCost,
		generateMutation,
	]);

	return {
		mode,
		setMode,
		setExample,
		model,
		setModel,
		modelConfig,
		inputImages,
		filledImages,
		maxImages,
		hasRequiredImages,
		setImageAt,
		addImageFiles,
		addExistingImages,
		removeImage,
		restoreImageAt,
		replaceImage,
		clearImages,
		referenceMedia,
		referenceVideos,
		referenceAudios,
		addReferenceMedia,
		setDrivingAudio,
		removeReferenceMedia,
		restoreReferenceMedia,
		videoLimits,
		audioLimits,
		referenceVideoSeconds,
		isProcessingMedia,
		prompt,
		setPrompt,
		resolution,
		setResolution,
		lockedResolutions,
		aspectRatio,
		canPickAspectRatio,
		setAspectRatio,
		duration,
		setDuration,
		generateAudio,
		setGenerateAudio,
		outputs,
		setOutputs,
		creditCost,
		credits,
		isPending,
		isUploading,
		applyHistoryItem,
		requestLogin,
		handleGenerate,
		latestStartedBatchId,
		resumedBatchId,
		history,
		isHistoryLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		paywallReason,
		setPaywallReason,
	};
}
